import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import { Box, Button, Stack, InputLabel, TextField, Typography, Divider, Autocomplete, Switch } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useParams } from "react-router-dom";
import { addOrUpdateNews, deleteNews, notifyUsersAboutNews, getNewsById } from "../../Requests/NewsService";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { NewsStoreContext } from "../../Store/NewsStore";
import ImageDropzone from "../StyledComponents/AvatarDropzone/ImageDropzone.tsx";
import { DeleteForever, NotificationsActive, ArrowBack, FileDownload } from "@mui/icons-material";
import { exportDataToCsv } from "../../Utils/exportTableToCsv.js";
import { getUsers } from "../../Requests/UserService.ts";
import CircularProgressView from "../StyledComponents/CircularProgressView";
import { News } from "../../Models/News";
import HtmlEditor from '../HtmlEditorView/HtmlEditor.jsx';


export default function NewsInfo() {

  const navigate = useNavigate()
  const { newsid } = useParams()
  const store = useContext(NewsStoreContext)
  const [isLoading, setIsLoading] = useState(true)

  const [heroImage, setHeroImage] = useState()
  const [title, setTitle] = useState()
  const [newsDate, setNewsDate] = useState()
  const [source, setSource] = useState()
  const [description, setDescription] = useState()
  const [buttonLink, setButtonLink] = useState()
  const [hidden, setHidden] = useState()
  const [createdAt, setCreatedAt] = useState()
  const [updatedAt, setUpdatedAt] = useState()

  const [members, setMembers] = useState()
  const [allmembers, setAllmembers] = useState([])

  const headerRef = useRef(null)
  const APP_BAR_HEIGHT = 64
  const [headerHeight, setHeaderHeight] = useState(0)

  useEffect(() => {
    if (!headerRef.current) return

    const updateHeight = () => {
      setHeaderHeight(headerRef.current?.offsetHeight ?? 0)
    }

    updateHeight()

    const observer = new ResizeObserver(updateHeight)
    observer.observe(headerRef.current)

    return () => observer.disconnect()
  }, [])

  const HEADER_BOTTOM_OFFSET = APP_BAR_HEIGHT + headerHeight

  const fetchData = useCallback(async () => {
    const users = await getUsers()
    const news = await getNewsById(newsid)

    setHeroImage(news.heroImage)
    setTitle(news.title)
    setNewsDate(news.newsDate)
    setSource(news.source)
    setDescription(news.description)
    setButtonLink(news.sourceLink)
    setHidden(news.hidden)
    setCreatedAt(news.createdAt)
    setUpdatedAt(news.updatedAt)
    setMembers(news.mentionedMembers)

    setAllmembers(users)

    setIsLoading(false)
  }, [newsid])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleSaveChanges = async () => {
    let news = new News()
    news.objectId = newsid
    news.title = title
    news.newsDate = dayjs(newsDate).toDate()
    news.source = source
    news.description = description
    news.sourceLink = buttonLink
    news.mentionedMembers = members
    news.hidden = hidden
    await addOrUpdateNews(news, heroImage)
    // Mark store as needing refresh
    store.needsRefresh = true
    navigate("/news")
  }

  const getNewsForExport = () => ({
    objectId: newsid,
    heroImage,
    title,
    newsDate,
    source,
    description,
    sourceLink: buttonLink,
    hidden,
    createdAt,
    updatedAt,
    mentionedMembers: members,
  })

  const handleNotification = async () => {
    notifyUsersAboutNews(newsid, title)
  }

  const handleHiddenNews = async (event) => {
    setHidden(!event)
  }

  const handleDeleteNews = async () => {
    await deleteNews(newsid)
    // Mark store as needing refresh
    store.needsRefresh = true
    navigate("/news")
  }

  const loaderView = <CircularProgressView />

  const contentView = <>
    <Box
      ref={headerRef}
      sx={{
        position: "sticky",
        top: APP_BAR_HEIGHT,
        backgroundColor: "background.paper",
        zIndex: 100
      }}
    >
      <Box
        paddingTop={2}
        paddingBottom={2}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
          gap: 1,
          width: "calc(100% - 280px)",
          paddingRight: 2
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate("/news")}
            sx={{ minWidth: "auto", alignSelf: "flex-start" }}
          >
            Back
          </Button>
          <Typography
            variant="h4"
            sx={{
              maxWidth: "600px"
            }}
          >
            {title}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          onClick={() => handleSaveChanges()}
        >
          Save Changes
        </Button>
      </Box>
      <Divider />
    </Box>

    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between"
      }}
    >
      <Box
        width="calc(100% - 280px)"
        sx={{
          paddingRight: 2
        }}
      >
        <ImageDropzone
          title={"Hero Image"}
          imageUrl={heroImage}
          callbackFile={(file) => { setHeroImage(file) }}
        />

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"Title"}</InputLabel>
          <TextField
            placeholder="Title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </Stack>

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"Date"}</InputLabel>
          <DatePicker
            format="DD/MM/YYYY"
            value={dayjs(newsDate)}
            onChange={(newValue) => setNewsDate(newValue)}
          />
        </Stack>

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"Source"}</InputLabel>
          <TextField
            placeholder="Source"
            value={source}
            onChange={(event) => setSource(event.target.value)}
          />
        </Stack>

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"Members Mentioned"}</InputLabel>
          <Autocomplete
            multiple
            options={allmembers}
            getOptionLabel={(option) => option.name + " " + option.surname}
            filterSelectedOptions
            defaultValue={members}
            onChange={(event, value) => setMembers(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Members"
              />
            )}
          />
        </Stack>

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"Description"}</InputLabel>
          <HtmlEditor initialValue={description} onChange={setDescription} />
        </Stack>

        <Stack
          spacing={1}
          marginTop={4}
        >
          <InputLabel>{"Button Link URL"}</InputLabel>
          <TextField
            placeholder="Button Link URL"
            value={buttonLink}
            onChange={(event) => setButtonLink(event.target.value)}
          />
        </Stack>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          width: "280px",
          position: "fixed",
          right: 0,
          top: APP_BAR_HEIGHT + 1,
          bottom: 0,
          overflowY: "auto",
          backgroundColor: "background.paper",
          borderLeft: "1px solid",
          borderColor: "grey.200",
          zIndex: 110
        }}
      >
        <Divider orientation="vertical" />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            padding: 2
          }}
        >
          <Button
            size="small"
            startIcon={<FileDownload />}
            onClick={() =>
              exportDataToCsv([getNewsForExport()], newsid ? `news-${newsid}` : "news", {
                preferredKeys: ["title"],
              })
            }
            sx={{ mb: 2, alignSelf: "flex-start" }}
          >
            Export CSV
          </Button>
          <Box
            width="100%"
            display="flex"
            justifyContent="space-between"
          >
            <InputLabel>{"Date Created"}</InputLabel>
            <InputLabel>{dayjs(createdAt).format('DD/MMM/YYYY')}</InputLabel>
          </Box>

          <Box
            marginTop={1}
            width="100%"
            display="flex"
            justifyContent="space-between"
          >
            <InputLabel>{"Last Update"}</InputLabel>
            <InputLabel>{dayjs(updatedAt).format('DD/MMM/YYYY')}</InputLabel>
          </Box>

          <Box
            marginTop={1}
            marginBottom={2}
            width="100%"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <InputLabel>{"Visibility"}</InputLabel>
            <Switch
              checked={!hidden}
              onChange={(event) => handleHiddenNews(event.target.checked)}
            />
          </Box>

          <Divider />

          <Box
            sx={{
              marginTop: 2,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 1
            }}
          >
            <Button
              variant="outlined"
              onClick={() => handleNotification()}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1
                }}
              >
                <NotificationsActive />
                Push Notification
              </Box>
            </Button>

            <Divider />

            <Button
              variant="outlined"
              color="error"
              onClick={() => handleDeleteNews()}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1
                }}
              >
                <DeleteForever />
                Delete News
              </Box>
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  </>

  return (
    <>
      {isLoading ? loaderView : contentView}
    </>
  )
}
